/**
 * VerifiedCoverageChip — small trust chip that surfaces the
 * `verified-coverage` percentage from the public degree endpoint.
 *
 * Same number the Uni Portal shows to owners (see
 * `owner_list_degrees` on the backend). This is the family-facing
 * counterpart — so both audiences look at the exact same metric.
 *
 * We're deliberately quiet on low-coverage courses (< 40%) — the
 * chip renders in a muted amber tone rather than the marketing-y
 * green so families aren't misled into thinking a sparse page is
 * "certified".
 */
import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";

export default function VerifiedCoverageChip({ degreeId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!degreeId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await api.get(
          `/degrees/${degreeId}/verified-coverage`,
        );
        if (!cancelled) setData(d);
      } catch {
        // Missing coverage isn't user-actionable — swallow.
      }
    })();
    return () => { cancelled = true; };
  }, [degreeId]);

  if (!data || !data.verified_total) return null;

  const pct = Math.max(0, Math.min(100, data.verified_pct || 0));
  const good = pct >= 60;
  const okay = pct >= 40 && pct < 60;
  const tone = good
    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
    : okay
      ? "bg-amber-50 border-amber-200 text-amber-800"
      : "bg-stone-100 border-stone-300 text-stone-600";
  const barTone = good
    ? "bg-emerald-600"
    : okay
      ? "bg-amber-500"
      : "bg-stone-400";

  return (
    <TooltipProvider delayDuration={80}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-underground-sc tracking-wider ${tone} cursor-help`}
            data-testid="verified-coverage-chip"
          >
            <ShieldCheck className="h-3 w-3" />
            <span className="font-mono normal-case tracking-normal">
              {pct}% verified
            </span>
            <span
              className="h-1 w-8 rounded-full bg-white/70 overflow-hidden"
              aria-hidden="true"
            >
              <span
                className={`block h-full ${barTone}`}
                style={{ width: `${Math.max(3, pct)}%` }}
              />
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs text-xs">
          <div className="font-semibold">
            {data.verified_count} of {data.verified_total} data fields set
          </div>
          <div className="mt-1 opacity-80">
            Counts every fact this course could publish (fees, entry
            requirements, English tests, outcomes, admissions cycle…).
            Higher = more you can trust the numbers on this page.
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
